import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableSelectOption {
  id: number | string;
  name: string;
  /** Shown next to the name (e.g. how many freights the port has). */
  count?: number | null;
}

const normalize = (text: string): string =>
  (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/**
 * Select that narrows its options while the user types (Material autocomplete). Works as a form control
 * (`formControlName`) or with `[value]` / `(valueChange)`. `null` means nothing picked, or the `allLabel` option.
 */
@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchableSelectComponent), multi: true }],
})
export class SearchableSelectComponent implements ControlValueAccessor {
  /** null (e.g. an async source still loading) counts as no options. */
  @Input()
  set options(options: SearchableSelectOption[] | null) {
    this._options = options ?? [];
    // Options that arrive after the value (async load): show the name, never overwrite what is being typed.
    if (this.hasValue && !this.text) {
      this.text = this.nameOf(this._value);
    }
  }
  get options(): SearchableSelectOption[] {
    return this._options;
  }
  private _options: SearchableSelectOption[] = [];
  @Input() label = '';
  @Input() placeholder = '';
  /** When set, the list starts with this option and picking it clears the value (null). */
  @Input() allLabel: string | null = null;
  @Input() testId = 'searchable-select';

  @Input()
  set value(value: number | string | null) {
    this.writeValue(value);
  }
  get value(): number | string | null {
    return this._value;
  }
  @Output() valueChange = new EventEmitter<number | string | null>();

  text = '';
  @Input() disabled = false;
  private _value: number | string | null = null;
  private onChange: (value: number | string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get filtered(): SearchableSelectOption[] {
    const term = normalize(this.text);
    // Text equal to the picked option's name is not a search: show the whole list again.
    if (!term || this.text === this.nameOf(this._value)) {
      return this.options;
    }
    return this.options.filter((o) => normalize(o.name).includes(term));
  }

  get hasValue(): boolean {
    return this._value != null && this._value !== '';
  }

  writeValue(value: number | string | null): void {
    this._value = value === '' || value === undefined ? null : value;
    this.text = this.nameOf(this._value);
  }

  registerOnChange(fn: (value: number | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onInput(text: string): void {
    this.text = text;
  }

  pick(id: number | string | null): void {
    this._value = id;
    this.text = this.nameOf(id);
    this.onChange(id);
    this.valueChange.emit(id);
  }

  clear(): void {
    this.pick(null);
  }

  /** Text that is not an option goes back to the picked name; an emptied text clears the value. */
  onBlur(): void {
    this.onTouched();
    if (!this.text.trim() && this.hasValue) {
      this.pick(null);
      return;
    }
    this.text = this.nameOf(this._value);
  }

  trackById(_: number, option: SearchableSelectOption): number | string {
    return option.id;
  }

  private nameOf(id: number | string | null): string {
    if (id == null) {
      return '';
    }
    return this.options.find((o) => o.id === id || String(o.id) === String(id))?.name ?? '';
  }
}
